import { useEffect } from "react";
import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import { getPlans } from "api/plan-change";
import { getUserFriendlyErrorMessage } from "api/UserFriendlyError";
import { ReduxRootState } from "@/types/redux-store";

type PlansState = {
  data: Awaited<ReturnType<typeof getPlans>> | null;
  loading: boolean;
};

const featureKey = "plans" as const;

const initialState: PlansState = {
  data: null,
  loading: false,
};

function getFeatureState(state: ReduxRootState): PlansState {
  return (
    (state as ReduxRootState & { [featureKey]?: PlansState })[featureKey] ??
    initialState
  );
}

export const getPlansFx = createAsyncThunk("plans/load", async () => {
  try {
    return await getPlans();
  } catch (error) {
    toast.error(getUserFriendlyErrorMessage(error, "Could not get plans"));
    throw error;
  }
});

const plansSlice = createSlice({
  name: featureKey,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(getPlansFx.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getPlansFx.fulfilled, (state, action) => {
      state.loading = false;
      state.data = action.payload;
    });
    builder.addCase(getPlansFx.rejected, (state) => {
      state.loading = false;
    });
  },
});

export const plansReducer = plansSlice.reducer;

export const selectPlans = createSelector(
  getFeatureState,
  (plansState) => plansState.data
);

export const selectPlansLoading = createSelector(
  getFeatureState,
  (plansState) => plansState.loading
);

export function usePlans() {
  const dispatch = useDispatch();
  const plans = useSelector(selectPlans);
  const isLoading = useSelector(selectPlansLoading);

  useEffect(() => {
    dispatch(getPlansFx() as never);
  }, [dispatch]);

  return { plans, isLoading };
}
